import { JOB_DETAILS_PROVIDER_ID } from '../constants/common';

export type FeatureFlagT = ReturnType<typeof getFeatureFlags>;

export type GetFeatureFlagsProps = {
    shouldAlwaysShowProfileInsights?: boolean;
    shouldShowTypicallyRequiredProfileInsights?: boolean;
    shouldMergeMceDataProfileInsights?: boolean;
    shouldUseMceAttributes?: boolean;
    shouldHideAttributesWithoutRequirementStrength?: boolean;
    shouldHideStrictnessForInferredAttributes?: boolean;
    shouldLimitExpandedChipCount?: boolean;
    shouldOnlyDisplayMustHaveRequirements?: boolean;
    crowtaxoFrontendTstPayload?: any;
    country: string;
    language: string;
    isClientAppMobile?: boolean;
    providerId?: string;
    dsanr: boolean;
};

export const defaultFeatureFlags: FeatureFlagT = {
    shouldAlwaysShow: false,
    showMoreReqTypesAndStrictness: false,
    shouldMergeMceData: false,
    shouldShowTypicallyRequired: false,
    shouldDisablePersonalization: false,
    shouldUseMceAttributes: false,
    shouldHideProfileInsights: false,
    shouldShowCrowtaxoFrontend: false,
    shouldHideAttributesWithoutRequirementStrength: false,
    shouldHideStrictnessForInferredAttributes: false,
    shouldLimitExpandedChipCount: false,
    shouldOnlyDisplayMustHaveRequirements: false,
};

export function getFeatureFlags({
    shouldAlwaysShowProfileInsights,
    shouldShowTypicallyRequiredProfileInsights,
    shouldMergeMceDataProfileInsights,
    shouldUseMceAttributes,
    shouldHideAttributesWithoutRequirementStrength,
    shouldHideStrictnessForInferredAttributes,
    shouldLimitExpandedChipCount,
    shouldOnlyDisplayMustHaveRequirements,
    isClientAppMobile,
    language,
    country,
    providerId,
    dsanr,
    crowtaxoFrontendTstPayload,
}: GetFeatureFlagsProps) {
    function shouldAlwaysShow(): boolean {
        if (providerId === JOB_DETAILS_PROVIDER_ID) {
            return true;
        }

        return shouldAlwaysShowProfileInsights ?? false;
    }

    function shouldShowTypicallyRequired(): boolean {
        return shouldShowTypicallyRequiredProfileInsights ?? false;
    }

    function shouldMergeMceData(): boolean {
        return shouldMergeMceDataProfileInsights ?? false;
    }

    function isEnPrimaryLocale(): boolean {
        return language === 'en' && ['US', 'AU', 'CA', 'GB'].includes(country);
    }

    function shouldUseMceAttributesFlag(): boolean {
        return shouldUseMceAttributes ?? false;
    }

    function getCrowtaxoVjBlackout(): boolean {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return crowtaxoFrontendTstPayload?.vj_blackout_test;
    }

    function shouldHideProfileInsights(): boolean {
        const isVjBlackout = getCrowtaxoVjBlackout();
        return isVjBlackout === true;
    }

    function shouldShowCrowtaxoFrontend(): boolean {
        const isVjBlackout = getCrowtaxoVjBlackout();
        return isVjBlackout === false;
    }

    function getShouldHideAttributesWithoutRequirementStrength(): boolean {
        return shouldHideAttributesWithoutRequirementStrength ?? false;
    }

    function getShouldHideStrictnessForInferredAttributes(): boolean {
        return shouldHideStrictnessForInferredAttributes ?? false;
    }

    function getShouldLimitExpandedChipCount(): boolean {
        return shouldLimitExpandedChipCount ?? false;
    }

    function getShouldOnlyDisplayMustHaveRequirements(): boolean {
        return shouldOnlyDisplayMustHaveRequirements ?? false;
    }

    // This is for EU-DSA, in which we have to display Indeed with no personalization based on the user's profile. See LPR-7722 for some additional info. The user is also logged out when this is active.
    function shouldDisablePersonalization(): boolean {
        return dsanr;
    }

    return {
        shouldAlwaysShow: shouldAlwaysShow(),
        showMoreReqTypesAndStrictness: isClientAppMobile && isEnPrimaryLocale(),
        shouldMergeMceData: shouldMergeMceData(),
        shouldShowTypicallyRequired: shouldShowTypicallyRequired(),
        shouldDisablePersonalization: shouldDisablePersonalization(),
        shouldUseMceAttributes: shouldUseMceAttributesFlag(),
        shouldHideProfileInsights: shouldHideProfileInsights(),
        shouldShowCrowtaxoFrontend: shouldShowCrowtaxoFrontend(),
        shouldHideAttributesWithoutRequirementStrength:
            getShouldHideAttributesWithoutRequirementStrength(),
        shouldHideStrictnessForInferredAttributes: getShouldHideStrictnessForInferredAttributes(),
        shouldLimitExpandedChipCount: getShouldLimitExpandedChipCount(),
        shouldOnlyDisplayMustHaveRequirements: getShouldOnlyDisplayMustHaveRequirements(),
    };
}
